import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import FlexTable from "../../../components/ui-components/FlexTable";
import ConnectionStatusBadge from "./ConnectionStatusBadge";
import Badge from "../../../components/ui-components/Badge";
import { getSourceType } from "../sourceTypes";
import { formatRelativeTime } from "../../data-imports/utils";

const IMPORT_JOB_BADGE_MAP = {
  active: { status: "success", label: "Active" },
  paused: { status: "stopped", label: "Paused" },
  error: { status: "failed", label: "Error" },
  awaiting: { status: "pending", label: "Awaiting" },
};

// Connection-specific column definitions rendered through the shared FlexTable. The page owns the
// card chrome (TableCard) and the empty state; this component returns null when there's no data.
const ConnectionsTable = ({ data, onRowClick, onEdit, onDelete, onRevalidate }) => {
  const columns = useMemo(
    () => [
      {
        accessor: "source_type",
        Header: () => <span>Source</span>,
        Cell: ({ row }) => {
          const sourceType = getSourceType(row.original.source_type);
          return (
            <span className="font-medium text-text">{sourceType?.name || row.original.source_type}</span>
          );
        },
        minWidth: 180,
        width: 200,
      },
      {
        accessor: "connection_name",
        Header: () => <span>Connection Name</span>,
        Cell: ({ value }) => <span className="text-text truncate">{value}</span>,
        minWidth: 160,
        flex: 1,
      },
      {
        accessor: "auth_method",
        Header: () => <span>Auth Method</span>,
        Cell: ({ row }) => {
          const sourceType = getSourceType(row.original.source_type);
          return (
            <span className="text-text-2">
              {sourceType?.credentialLabel || row.original.auth_method}
            </span>
          );
        },
        width: 150,
        minWidth: 120,
      },
      {
        id: "status",
        Header: () => <span>Status</span>,
        Cell: ({ row }) => <ConnectionStatusBadge status={row.original.credential_status} />,
        width: 110,
        minWidth: 90,
      },
      {
        id: "last_sync",
        Header: () => <span>Last Sync</span>,
        Cell: ({ row }) => (
          <span className="text-text-2">{formatRelativeTime(row.original.last_sync_at)}</span>
        ),
        width: 100,
        minWidth: 80,
      },
      {
        id: "import_job",
        Header: () => <span>Import Job</span>,
        Cell: ({ row }) => {
          const status = row.original.import_job_status || "awaiting";
          const badge = IMPORT_JOB_BADGE_MAP[status] || IMPORT_JOB_BADGE_MAP.awaiting;
          return <Badge status={badge.status} label={badge.label} />;
        },
        width: 100,
        minWidth: 90,
      },
      {
        id: "actions",
        align: "right",
        Header: () => <span>Actions</span>,
        Cell: ({ row }) => (
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              className="text-text-2 hover:text-text hover:bg-surface-2 rounded-ctrl px-1 transition-colors"
              title="Edit"
              aria-label="Edit connection"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(row.original);
              }}
            >
              <FontAwesomeIcon icon={icon({ name: "pen-to-square", style: "light" })} className="w-4 h-4" />
            </button>
            <button
              type="button"
              className="text-text-2 hover:text-text hover:bg-surface-2 rounded-ctrl px-1 transition-colors"
              title="Re-validate"
              aria-label="Re-validate connection"
              onClick={(e) => {
                e.stopPropagation();
                onRevalidate(row.original.id);
              }}
            >
              <FontAwesomeIcon icon={icon({ name: "rotate", style: "light" })} className="w-4 h-4" />
            </button>
            <button
              type="button"
              className="text-text-2 hover:text-danger hover:bg-danger-soft rounded-ctrl px-1 transition-colors"
              title="Delete"
              aria-label="Delete connection"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(row.original);
              }}
            >
              <FontAwesomeIcon icon={icon({ name: "trash-can", style: "light" })} className="w-4 h-4" />
            </button>
          </div>
        ),
        width: 130,
        minWidth: 120,
      },
    ],
    [onEdit, onDelete, onRevalidate]
  );

  if (!data || data.length === 0) {
    return null; // Empty state is handled by the parent page
  }

  return (
    <FlexTable
      columns={columns}
      data={data}
      onRowClick={(id) => onRowClick(id)}
      getRowId={(c) => c.id}
      rowAriaLabel={(c) => `Open connection ${c.connection_name || c.id}`}
    />
  );
};

ConnectionsTable.propTypes = {
  data: PropTypes.array,
  onRowClick: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onRevalidate: PropTypes.func,
};

export default ConnectionsTable;
